import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, MapPin, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { api } from "@/lib/api";
import { useDebounced } from "@/hooks/useDirectory";
import { slugify } from "@/lib/format";
import { cn } from "@/lib/utils";

function Trigger({ label, count, testId, icon: Icon, renderLabel, ...props }) {
  const displayText = renderLabel ? renderLabel(count) : label;
  return (
    <button
      type="button"
      data-testid={testId}
      {...props}
      className={cn(
        "inline-flex h-9 items-center gap-1.5 rounded-full border px-3.5 text-sm font-semibold transition-colors",
        count ? "border-primary/40 bg-accent text-primary" : "border-border bg-white text-stone-700 hover:border-stone-300 hover:bg-stone-50",
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      <span>{displayText}</span>
      {!renderLabel && count > 0 && <span className="rounded-full bg-primary px-1.5 text-[11px] font-bold text-white">{count}</span>}
      <ChevronDown className="h-3.5 w-3.5 opacity-60" />
    </button>
  );
}

const Box = ({ on }) => (
  <span className={cn("mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded border", on ? "border-primary bg-primary text-white" : "border-stone-300")}>
    {on && <Check className="h-3 w-3" />}
  </span>
);

export function MultiSelectFilter({ label, options = [], selected = [], onChange, testId, icon, renderLabel, searchPlaceholder = "Rechercher…", widthClass = "w-72" }) {
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Trigger label={label} count={selected.length} testId={testId} icon={icon} renderLabel={renderLabel} />
      </PopoverTrigger>
      <PopoverContent className={cn("p-0", widthClass)} align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} data-testid={`${testId}-search`} />
          <CommandList className="max-h-72">
            <CommandEmpty>Aucune option</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem key={o.value} value={`${o.label} ${o.subtitle || ""} ${o.value}`} onSelect={() => toggle(o.value)}
                  data-testid={`${testId}-option-${slugify(o.label)}`}
                  className="flex items-center justify-between gap-2"
                >
                  <div className="flex items-center min-w-0 flex-1">
                    <Box on={selected.includes(o.value)} />
                    {o.color && <span className="mr-2 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: o.color }} />}
                    <span className="truncate" title={o.label}>{o.label}</span>
                  </div>
                  {o.subtitle && (
                    <span className="shrink-0 rounded bg-stone-100 px-1.5 py-0.5 text-[10px] font-semibold text-stone-500">
                      {o.subtitle}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {selected.length > 0 && (
            <button type="button" onClick={() => onChange([])} className="w-full border-t px-3 py-2 text-left text-xs font-semibold text-muted-foreground hover:text-foreground">
              Effacer la sélection ({selected.length})
            </button>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function CityFilter({ value = "", onChange, placeholder = "Rechercher une ville…", testId = "filter-city-input" }) {
  const [text, setText] = useState(value || "");
  const debounced = useDebounced(text, 300);

  useEffect(() => {
    setText(value || "");
  }, [value]);

  useEffect(() => {
    if (debounced !== (value || "")) {
      onChange(debounced);
    }
  }, [debounced, onChange, value]);

  return (
    <div className="relative flex items-center">
      <MapPin className="pointer-events-none absolute left-3.5 h-3.5 w-3.5 text-stone-400" />
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className={cn(
          "h-9 w-full sm:w-52 rounded-full border border-border bg-white pl-9 pr-7 text-sm placeholder:text-stone-400 font-medium",
          "focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors",
          text ? "border-primary/40 bg-accent/25 text-primary font-semibold" : "hover:border-stone-300"
        )}
      />
      {text ? (
        <button
          type="button"
          onClick={() => {
            setText("");
            onChange("");
          }}
          className="absolute right-2.5 rounded-full p-0.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700 transition-colors"
          aria-label="Effacer la ville"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}

export function OrgFilter({ selected = [], onChange, testId = "filter-organization", icon }) {
  const [text, setText] = useState("");
  const q = useDebounced(text, 200);
  const { data = [], isFetching } = useQuery({
    queryKey: ["org-autocomplete", q],
    queryFn: async () => (await api.get(`/organizations/autocomplete?q=${encodeURIComponent(q)}&limit=12`)).data,
    enabled: q.length > 0,
  });
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Trigger label="Entreprise" count={selected.length} testId={testId} icon={icon} />
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Tapez un nom d'entreprise…" value={text} onValueChange={setText} data-testid={`${testId}-search`} />
          <CommandList>
            {!q && <div className="px-3 py-4 text-xs text-muted-foreground">Commencez à taper pour rechercher (ex : Airbus).</div>}
            {q && !isFetching && !data.length && <CommandEmpty>Aucune entreprise trouvée</CommandEmpty>}
            <CommandGroup>
              {data.map((o) => (
                <CommandItem key={o.id} value={o.id} onSelect={() => toggle(o.id)} data-testid={`${testId}-option-${slugify(o.name)}`}>
                  <Box on={selected.includes(o.id)} />
                  <span className="truncate">{o.name}</span>
                  {o.city && <span className="ml-auto pl-2 text-xs text-muted-foreground">{o.city}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {selected.length > 0 && (
            <button type="button" onClick={() => onChange([])} className="w-full border-t px-3 py-2 text-left text-xs font-semibold text-muted-foreground hover:text-foreground">
              Effacer la sélection ({selected.length})
            </button>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function ActiveChips({ chips, onRemove, onClear }) {
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="active-filters">
      <span className="text-xs font-bold uppercase tracking-wider text-stone-500">Filtres actifs :</span>
      {chips.map((chip) => (
        <span
          key={`${chip.key}-${chip.value}`}
          data-testid={`active-filter-chip-${chip.key}-${slugify(chip.label)}`}
          className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-accent/30 py-1 pl-3 pr-1.5 text-xs font-semibold text-primary"
        >
          <span className="text-stone-500 font-normal">{chip.group} :</span> {chip.label}
          <button
            type="button"
            onClick={() => onRemove(chip)}
            aria-label={`Retirer le filtre ${chip.label}`}
            data-testid={`remove-filter-${chip.key}-${slugify(chip.label)}`}
            className="rounded-full p-0.5 hover:bg-primary/10 text-primary/70 hover:text-primary transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={onClear}
        data-testid="clear-all-filters-button"
        className="rounded-full px-2.5 py-1 text-xs font-semibold text-primary/80 hover:bg-accent hover:text-primary transition-colors underline-offset-4 hover:underline"
      >
        Réinitialiser les filtres
      </button>
    </div>
  );
}
